/***************************************************************************************
 * FileName      : DeviceHighlightService.ts
 * Description   : 구성요소 부품 클릭 시 해당 부품 위치에 3D 테두리 박스 표시
 *                 - 커스텀 mp.deviceOutline 컴포넌트 사용
 *                 - 12개 엣지를 dash(점선) 조각으로 분할하여 WebGL linewidth 제약 우회
 *                 - 반투명 내부 채우기는 옵션 (fillVisible=true 시에만 렌더링)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.04.15
 * Modified Date : 2026.04.24 : 박은정 : 점선 엣지 / 선 두께 축소(0.005) / 채우기 옵션화
 * Reference     : packages/common/src/sdk-components/OrientedBox.ts
 ***************************************************************************************/

import { SceneComponent } from '@mp/common';

// ============================================================================
// 타입 정의
// ============================================================================

export interface HighlightConfig {
  /** 3D 월드 좌표 (anchorPosition) */
  position: { x: number; y: number; z: number };
  /** 박스 크기 (미터 단위, 미지정 시 기본값 사용) */
  boxSize?: { x: number; y: number; z: number };
  /** 박스 위치 오프셋 (anchorPosition 기준 상대 좌표, 미지정 시 0,0,0) */
  boxOffset?: { x: number; y: number; z: number };
  /** 반투명 면 채우기 표시 여부 (기본 false — 선만 렌더링) */
  fillVisible?: boolean;
}

// ============================================================================
// 기본값
// ============================================================================

const DEFAULT_BOX_SIZE = { x: 0.45, y: 0.45, z: 0.45 };
/** 테두리 선 두께 (미터 단위) */
const LINE_THICKNESS = 0.005;
/** 테두리 색상 (주황색) */
const HIGHLIGHT_COLOR = 0xff7700;
/** 채우기 투명도 (fillVisible=true 일 때만 사용) */
const FILL_OPACITY = 0.18;
/** 점선(dash) 패턴 — 목표 대시 길이 / 간격 (미터). 엣지 길이에 맞춰 비율 유지하며 자동 스케일. */
const DASH_TARGET = 0.03;
const GAP_TARGET  = 0.015;

// ============================================================================
// 커스텀 씬 컴포넌트 (mp.deviceOutline)
// - 12개 엣지를 dash 조각으로 분할하여 점선 테두리 구현
// - 내부 반투명 면은 fillOpacity > 0 일 때만 가시
// ============================================================================

const OUTLINE_COMPONENT_TYPE = 'mp.deviceOutline';

interface OutlineInputs {
  size: { x: number; y: number; z: number };
  color: number;
  thickness: number;
  fillOpacity: number;
}

class DeviceOutlineComponent extends SceneComponent {
  inputs: OutlineInputs = {
    size: { x: 0.5, y: 0.5, z: 0.5 },
    color: 0xff7700,
    thickness: 0.005,
    fillOpacity: 0,
  };

  private root: any = null;
  private edgeMeshes: any[] = [];
  private fillMesh: any = null;
  private edgeMat: any = null;
  private fillMat: any = null;

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.outputs.objectRoot = this.root;
    this.outputs.collider = this.root;
    this.build();
  }

  onInputsUpdated(_prev: OutlineInputs) {
    this.clear();
    this.build();
  }

  onDestroy() {
    this.clear();
  }

  private build() {
    const THREE = this.context.three;
    const { x: sx, y: sy, z: sz } = this.inputs.size;
    const t = this.inputs.thickness;

    // 엣지 메시용 재질
    this.edgeMat = new THREE.MeshBasicMaterial({
      color: this.inputs.color,
      transparent: false,
      depthWrite: false,
    });

    // 채우기 메시용 재질 (반투명 — 정상 블렌딩으로 명확한 색상 표현)
    this.fillMat = new THREE.MeshBasicMaterial({
      color: this.inputs.color,
      transparent: true,
      opacity: this.inputs.fillOpacity,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    // 채우기 박스
    const fillGeo = new THREE.BoxGeometry(sx, sy, sz);
    this.fillMesh = new THREE.Mesh(fillGeo, this.fillMat);
    this.root.add(this.fillMesh);

    // 12개 엣지 — 각 엣지를 dash 조각들로 분할 (X/Y/Z축 각 4개)
    const halfX = sx / 2;
    const halfY = sy / 2;
    const halfZ = sz / 2;

    const edges: { axis: 'x' | 'y' | 'z'; length: number; center: [number, number, number] }[] = [
      // X축 엣지
      { axis: 'x', length: sx, center: [0,  halfY,  halfZ] },
      { axis: 'x', length: sx, center: [0, -halfY,  halfZ] },
      { axis: 'x', length: sx, center: [0,  halfY, -halfZ] },
      { axis: 'x', length: sx, center: [0, -halfY, -halfZ] },
      // Y축 엣지
      { axis: 'y', length: sy, center: [ halfX, 0,  halfZ] },
      { axis: 'y', length: sy, center: [-halfX, 0,  halfZ] },
      { axis: 'y', length: sy, center: [ halfX, 0, -halfZ] },
      { axis: 'y', length: sy, center: [-halfX, 0, -halfZ] },
      // Z축 엣지
      { axis: 'z', length: sz, center: [ halfX,  halfY, 0] },
      { axis: 'z', length: sz, center: [-halfX,  halfY, 0] },
      { axis: 'z', length: sz, center: [ halfX, -halfY, 0] },
      { axis: 'z', length: sz, center: [-halfX, -halfY, 0] },
    ];

    for (const edge of edges) {
      this.addDashedEdge(edge.axis, edge.length, edge.center, t);
    }
  }

  /**
   * 주어진 엣지를 dash 조각들로 분할해 root에 추가.
   * - n 개의 dash + (n-1) 개의 gap 이 엣지 길이에 정확히 맞도록 스케일
   * - DASH_TARGET:GAP_TARGET 비율 유지, 양 끝은 dash 로 마감 (코너 닫힘)
   */
  private addDashedEdge(
    axis: 'x' | 'y' | 'z',
    length: number,
    center: [number, number, number],
    thickness: number
  ): void {
    const THREE = this.context.three;
    const ratio = DASH_TARGET / GAP_TARGET;
    const n = Math.max(2, Math.round((length + GAP_TARGET) / (DASH_TARGET + GAP_TARGET)));
    // nd + (n-1)g = length, d = ratio * g  ⇒  g = length / (n*ratio + n - 1)
    const g = length / (n * ratio + n - 1);
    const d = ratio * g;
    const t = thickness;
    const [cx, cy, cz] = center;

    for (let i = 0; i < n; i++) {
      const offsetOnAxis = -length / 2 + i * (d + g) + d / 2;

      let size: [number, number, number];
      let pos: [number, number, number];
      if (axis === 'x') {
        size = [d, t, t];
        pos = [cx + offsetOnAxis, cy, cz];
      } else if (axis === 'y') {
        size = [t, d, t];
        pos = [cx, cy + offsetOnAxis, cz];
      } else {
        size = [t, t, d];
        pos = [cx, cy, cz + offsetOnAxis];
      }

      const geo = new THREE.BoxGeometry(...size);
      const mesh = new THREE.Mesh(geo, this.edgeMat);
      mesh.position.set(...pos);
      this.root.add(mesh);
      this.edgeMeshes.push(mesh);
    }
  }

  private clear() {
    for (const mesh of this.edgeMeshes) {
      this.root.remove(mesh);
      mesh.geometry.dispose();
    }
    this.edgeMeshes = [];

    if (this.fillMesh) {
      this.root.remove(this.fillMesh);
      this.fillMesh.geometry.dispose();
      this.fillMesh = null;
    }

    if (this.edgeMat) {
      this.edgeMat.dispose();
      this.edgeMat = null;
    }
    if (this.fillMat) {
      this.fillMat.dispose();
      this.fillMat = null;
    }
  }
}

// ============================================================================
// DeviceHighlightService 클래스
// ============================================================================

/**
 * 구성요소 부품 3D 테두리 하이라이트 서비스
 * - 부품 클릭 시 anchorPosition 기준으로 mp.deviceOutline 배치
 * - 재클릭/카드 전환 시 제거
 */
export class DeviceHighlightService {
  private static instance: DeviceHighlightService;
  private sdk: any = null;
  private highlightNode: any = null;
  /** 컴포넌트 등록 여부 */
  private componentRegistered = false;

  static getInstance(): DeviceHighlightService {
    if (!DeviceHighlightService.instance) {
      DeviceHighlightService.instance = new DeviceHighlightService();
    }
    return DeviceHighlightService.instance;
  }

  /**
   * SDK 인스턴스 초기화 및 커스텀 컴포넌트 등록
   */
  async initialize(sdk: any): Promise<void> {
    this.sdk = sdk;
    if (!this.componentRegistered) {
      await sdk.Scene.register(OUTLINE_COMPONENT_TYPE, () => new DeviceOutlineComponent());
      this.componentRegistered = true;
    }
  }

  /**
   * 지정 위치에 3D 테두리 박스 표시
   */
  async showHighlight(config: HighlightConfig): Promise<void> {
    if (!this.sdk) {
      console.warn('[DeviceHighlightService] SDK가 초기화되지 않았습니다.');
      return;
    }

    await this.removeHighlight();

    const size = config.boxSize ?? DEFAULT_BOX_SIZE;
    const offset = config.boxOffset ?? { x: 0, y: 0, z: 0 };
    const px = config.position.x + offset.x;
    const py = config.position.y + offset.y;
    const pz = config.position.z + offset.z;
    // 2026.04.24 : 박은정 : 내부 채우기 표시 옵션 (기본 false — 선만 표시)
    const fillOpacity = config.fillVisible ? FILL_OPACITY : 0;

    try {
      const node = await this.sdk.Scene.createNode();
      node.position.set(px, py, pz);
      node.addComponent(OUTLINE_COMPONENT_TYPE, {
        size,
        color: HIGHLIGHT_COLOR,
        thickness: LINE_THICKNESS,
        fillOpacity,
      });
      node.start();
      this.highlightNode = node;
    } catch (error) {
      console.error('[DeviceHighlightService] 하이라이트 생성 실패:', error);
    }
  }

  /**
   * 현재 표시 중인 하이라이트 박스 제거
   */
  async removeHighlight(): Promise<void> {
    if (this.highlightNode) {
      try {
        this.highlightNode.stop();
      } catch (error) {
        console.warn('[DeviceHighlightService] 하이라이트 제거 실패:', error);
      }
      this.highlightNode = null;
    }
  }

  /**
   * 하이라이트 표시 여부 확인
   */
  isHighlightVisible(): boolean {
    return this.highlightNode !== null;
  }
}
